import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PostCard from "@/components/feed/PostCard";
import { ImageSquare, LinkSimple, PaperPlaneRight, Newspaper, Users, User as UserIcon } from "@phosphor-icons/react";

const TABS = [
  { key: "all", label: "All Ads", icon: Newspaper },
  { key: "following", label: "Following", icon: Users },
  { key: "mine", label: "My Posts", icon: UserIcon },
];

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  async function load(currentTab = tab) {
    setLoading(true);
    try {
      const { data } = await api.get(`/feed?filter=${currentTab}&limit=30`);
      setPosts(data.posts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(tab); }, [tab]);

  async function submitPost(e) {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/feed", { body: body.trim(), url: url.trim(), image_url: imageUrl });
      setPosts((p) => [data, ...p]);
      setBody(""); setUrl(""); setImageUrl("");
      toast.success("Ad posted to the feed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/post-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(data.url);
      toast.success("Image attached");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function onDelete(id) {
    setPosts((prev) => prev.filter((p) => p.id !== id));
  }
  function onUpdate(next) {
    setPosts((prev) => prev.map((p) => (p.id === next.id ? { ...p, ...next } : p)));
  }

  const previewSrc = imageUrl?.startsWith("/api/") ? `${API_BASE.replace(/\/api$/, "")}${imageUrl}` : imageUrl;

  return (
    <DashboardLayout>
      <div className="grid lg:grid-cols-4 gap-4">
        <aside className="lg:col-span-1 space-y-3">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Feed</div>
            <div className="space-y-1">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`feed-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                    tab === t.key
                      ? "bg-blue-700 text-white"
                      : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  <t.icon size={18} weight="bold" /> {t.label}
                </button>
              ))}
              <Link
                to="/discover"
                data-testid="feed-goto-discover"
                className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200"
              >
                <Users size={18} weight="bold" /> Discover People
              </Link>
            </div>
          </div>
        </aside>

        <section className="lg:col-span-3 space-y-4">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <form onSubmit={submitPost} className="space-y-3" data-testid="feed-composer">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-sm font-black overflow-hidden">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url.startsWith("/api/") ? `${API_BASE.replace(/\/api$/, "")}${user.avatar_url}` : user.avatar_url}
                      alt=""
                      className="h-11 w-11 object-cover"
                      data-testid="feed-composer-avatar-img"
                    />
                  ) : (
                    (user?.username || "?").slice(0, 1).toUpperCase()
                  )}
                </div>
                <textarea
                  data-testid="feed-composer-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={`What's your ad about, ${user?.username || "friend"}? Share a link and pitch it to the community.`}
                  className="flex-1 min-h-[80px] rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={2000}
                />
              </div>
              <div className="flex items-center gap-2 pl-14 flex-wrap">
                <div className="flex-1 min-w-[200px] flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1.5">
                  <LinkSimple size={16} weight="bold" className="text-slate-500" />
                  <input
                    data-testid="feed-composer-url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://your-link.com (optional)"
                    className="flex-1 bg-transparent text-sm focus:outline-none"
                  />
                </div>
                <label
                  data-testid="feed-composer-image-btn"
                  className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200 transition-colors duration-200"
                >
                  <ImageSquare size={16} weight="bold" />
                  {uploading ? "Uploading…" : imageUrl ? "Change image" : "Add image"}
                  <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" />
                </label>
                <button
                  type="submit"
                  disabled={posting || !body.trim()}
                  data-testid="feed-composer-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-5 py-2 text-sm transition-colors duration-200"
                >
                  <PaperPlaneRight size={16} weight="bold" />
                  {posting ? "Posting…" : "Post"}
                </button>
              </div>
              {previewSrc && (
                <div className="pl-14">
                  <div className="relative inline-block">
                    <img src={previewSrc} alt="preview" className="max-h-56 rounded-xl border border-slate-200 dark:border-slate-700" />
                    <button
                      type="button"
                      onClick={() => setImageUrl("")}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 hover:bg-red-400 text-white text-xs font-bold"
                      data-testid="feed-composer-image-remove"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          {loading && <div className="text-sm text-slate-500 dark:text-slate-400">Loading feed…</div>}
          {!loading && posts.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center">
              <div className="text-3xl font-black tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                Nothing here yet
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                {tab === "following"
                  ? "Follow some advertisers to see their posts in this tab."
                  : "Be the first to post an ad to the community feed."}
              </div>
            </div>
          )}

          <div className="space-y-4" data-testid="feed-post-list">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onDelete={onDelete} onUpdate={onUpdate} />
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
